// Tip of the day (GET /api/content/tip): one short line chosen by the baby's corrected age,
// the same all day in the family's time zone. Mountable on the Plan tab as <TipCard variant="compact" />.
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/lib/session';
import { contentPath, dayIn, useResource, type TipResp } from './data';
import { Icon } from './icons';
import { DraftTag } from './parts';
import './learn.css';

type Props = {
  /** full: Learn home (shows a quiet placeholder while loading). compact: a slim card for the Plan tab; renders nothing until loaded and hides on short phones. */
  variant?: 'full' | 'compact';
};

export function TipCard({ variant = 'full' }: Props) {
  const { t } = useTranslation('learn');
  const { baby, family } = useSession();
  const today = dayIn(family?.timezone);
  // The tip changes once a day: a copy from today is reused without asking again (and without a second server tip_view).
  const r = useResource<TipResp>(baby ? contentPath('/tip', { babyId: baby.id }) : null, { fresh: (c) => c.data.date === today });
  const tip = r.data?.tip; // an older copy is still a fine tip when offline

  if (!tip) {
    if (variant === 'full' && r.loading && !r.data) return <div className="tip-card surface felt is-loading" aria-hidden="true"><span /><span /></div>;
    return null;
  }

  const full = variant === 'full';
  return (
    <aside className={`tip-card surface felt${full ? '' : ' is-compact'}`} aria-labelledby="tip-of-day">
      <p className="eyebrow" id="tip-of-day">
        <Icon name="bulb" />
        {t('tip.title')}
        <span className="tip-topic">· {t(`tip.topic.${tip.topic}`)}</span>
        <DraftTag draft={tip.draft} />
      </p>
      <p className="tip-text">{tip.text}</p>
      {full && (
        <div className="tip-actions">
          {/* /chat?q= fills the chat box with the tip (never sends by itself) */}
          <Link className="lg-soft-btn tip-btn" to={`/chat?q=${encodeURIComponent(tip.text)}`}>{t('tip.ask')}</Link>
          {tip.phaseId && (
            <Link className="tip-link" to={`/learn/phases/${tip.phaseId}`}>
              {t('tip.aboutPhase')}
              <Icon name="chevron" />
            </Link>
          )}
        </div>
      )}
    </aside>
  );
}
